import {ExtendedStreamingInfo, InfoProps, useDebounce} from "../util/util";
import React, {useEffect, useState} from "react";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css'
import {TopTable} from "./top-table";
import {AllCharts} from "./charts";
import {addCommaToNumber} from "./stats";
import {doSearch, getStats} from "./actions";
import {DecadeBreakdown, ModeYear, SearchResult, TableType, TimeRange, TopItem} from "./types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClock, faGlobe, faHashtag, faUser} from "@fortawesome/free-solid-svg-icons";
import {EntityModal, EntityType} from "./entity-modal";

const DEFAULT_TIME_RANGES = [TimeRange.AllTime, TimeRange.Last7Days, TimeRange.Last30Days, TimeRange.YTD, TimeRange.Custom];

const selectorOption = 'py-2 px-4 border-t border-b border-mm-text flex items-center justify-center text-center';
const selectorSelected = 'bg-mm-text text-mm-dark';
const selectorUnselected = 'bg-mm-dark text-mm-text';
const cornerLeft = 'rounded-l-[0.35rem] border-l border-mm-text';
const cornerRight = 'rounded-r-[0.35rem] border-r border-mm-text';

export const MMStats = () => {

    // STYLING VARIABLES
    const [trackStyle, setTrackStyle] = useState(selectorSelected);
    const [artistStyle, setArtistStyle] = useState(selectorUnselected);
    const [albumStyle, setAlbumStyle] = useState(selectorUnselected);
    const [chartStyle, setChartStyle] = useState(selectorUnselected);
    const [countStyle, setCountStyle] = useState(selectorSelected);
    const [timeStyle, setTimeStyle] = useState(selectorUnselected);
    const [userSpecificStyle, setUserSpecificStyle] = useState(selectorSelected);
    const [userAgnosticStyle, setUserAgnosticStyle] = useState(selectorUnselected);
    const [showControlPanel, setShowControlPanel] = useState(false);
    const [showCountOrTimeSelector, setShowCountOrTimeSelector] = useState(true);

    // TIME VARIABLES
    const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange.AllTime);
    const [startTime, setStartTime] = useState<number | undefined>(undefined);
    const [endTime, setEndTime] = useState<number | undefined>(undefined);
    const [selectedStartDate, setSelectedStartDate] = useState(new Date());
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());

    // DATA VARIABLES (only fetched once, when the Stats component loads)
    const [averageLength, setAverageLength] = useState<string>('0');
    const [averagePopularity, setAveragePopularity] = useState([]);
    const [averageYear, setAverageYear] = useState(0);
    const [decadeBreakdown, setDecadeBreakdown] = useState<string[][]>([]);
    const [hourBreakdown, setHourBreakdown] = useState([]);
    const [medianYear, setMedianYear] = useState(0);
    const [modeYear, setModeYear] = useState<ModeYear[]>([]);
    const [percentExplicit, setPercentExplicit] = useState<string>('0');
    const [topAlbums, setTopAlbums] = useState([]);
    const [topAlbumsTime, setTopAlbumsTime] = useState([]);
    const [topArtists, setTopArtists] = useState([]);
    const [topArtistsTime, setTopArtistsTime] = useState([]);
    const [topTracks, setTopTracks] = useState([]);
    const [topTracksTime, setTopTracksTime] = useState([]);
    const [totalMinutes, setTotalMinutes] = useState<string>('0');
    const [totalTracks, setTotalTracks] = useState<string>('0');
    const [uniqueAlbums, setUniqueAlbums] = useState<string>('0');
    const [uniqueArtists, setUniqueArtists] = useState<string>('0');
    const [uniqueTracks, setUniqueTracks] = useState<string>('0');
    const [weekDayBreakdown, setWeekDayBreakdown] = useState<string[][]>([]);

    // OTHER
    const [isLandscape, setIsLandscape] = useState<boolean>();
    const [currentData, setCurrentData] = useState(<StatsInfo text={'Loading...'}/>);
    const [popupVisible, setPopupVisible] = useState(true);
    const [useUserAgnosticStats, setUseUserAgnosticStats] = useState(false);
    const [ignoreData, setIgnoreData] = useState(false);
    const [entityModal, setEntityModal] = useState<React.ReactElement | undefined>(undefined);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(orientation: portrait)');
        setIsLandscape(!mediaQuery.matches);

        const handleOrientationChange = (event: MediaQueryListEvent) => {
            setIsLandscape(!event.matches);
        }

        mediaQuery.addEventListener('change', handleOrientationChange);
        return () => {
            mediaQuery.removeEventListener('change', handleOrientationChange);
        }
    }, []);

    useEffect(() => {
        if (timeRange === TimeRange.Custom && (!startTime || !endTime)) {
            return;
        }
        if (timeRange !== TimeRange.Custom) {
            setStartTime(undefined);
            setEndTime(undefined);
        }
        setShowControlPanel(false);
        setCurrentData(<StatsInfo text={'Loading...'}/>);
        getStats(timeRange, startTime, endTime, useUserAgnosticStats).then(data => {

            setShowControlPanel(true);
            if (data === 'No tracks found for this time period.') {
                setIgnoreData(true);
                setCurrentData(<StatsInfo text={'No listening history found for this time period.'}/>);
                return;
            }

            addRankColumn(data.topAlbums.items);
            addRankColumn(data.topAlbumsTime.items);
            addRankColumn(data.topArtists.items);
            addRankColumn(data.topArtistsTime.items);
            addRankColumn(data.topTracks.items);
            addRankColumn(data.topTracksTime.items);

            addEntityModalSetterToArray(setEntityModal, data.topTracks.items);
            addEntityModalSetterToArray(setEntityModal, data.topTracksTime.items);
            addEntityModalSetterToArray(setEntityModal, data.topArtists.items);
            addEntityModalSetterToArray(setEntityModal, data.topArtistsTime.items);
            addEntityModalSetterToArray(setEntityModal, data.topAlbums.items);
            addEntityModalSetterToArray(setEntityModal, data.topAlbumsTime.items);

            let minutes = Math.floor(data.averageLength.value / 60);

            setAverageLength(minutes + ':' + makeIntDoubleDigit(data.averageLength.value - minutes * 60));
            setAveragePopularity(data.averagePopularity.items);
            setAverageYear(data.averageYear.value);
            setDecadeBreakdown(convertDecadesToPieChartData(data.decadeBreakdown.items));
            setHourBreakdown(data.hourBreakdown.items);
            setMedianYear(data.medianYear.value);
            setModeYear(data.modeYear.items);
            setPercentExplicit(data.percentExplicit.value + '%');
            setTopAlbums(data.topAlbums.items);
            setTopAlbumsTime(data.topAlbumsTime.items);
            setTopArtists(data.topArtists.items);
            setTopArtistsTime(data.topArtistsTime.items);
            setTopTracks(data.topTracks.items);
            setTopTracksTime(data.topTracksTime.items);
            setTotalMinutes(addCommaToNumber(data.totalMinutes.value));
            setTotalTracks(addCommaToNumber(data.totalTracks.value));
            setUniqueAlbums(addCommaToNumber(data.uniqueAlbums.value));
            setUniqueArtists(addCommaToNumber(data.uniqueArtists.value));
            setUniqueTracks(addCommaToNumber(data.uniqueTracks.value));
            setWeekDayBreakdown(addWeekDayToData(data.weekDayBreakdown.items));

            setIgnoreData(false);
            setCurrentData(<TopTable items={data.topTracks.items} type={TableType.TrackCount}/>);

            setTrackStyle(selectorSelected);
            setArtistStyle(selectorUnselected);
            setAlbumStyle(selectorUnselected);
            setChartStyle(selectorUnselected);
            setShowCountOrTimeSelector(true);
            setCountStyle(selectorSelected);
            setTimeStyle(selectorUnselected);
            setCountStyle(selectorSelected);
            setTimeStyle(selectorUnselected);
        });
    }, [timeRange, startTime, endTime, useUserAgnosticStats]);

    const setToTrack = () => {
        setTrackStyle(selectorSelected);
        setArtistStyle(selectorUnselected);
        setAlbumStyle(selectorUnselected);
        setChartStyle(selectorUnselected);
        setShowCountOrTimeSelector(true);
        setCountStyle(selectorSelected);
        setTimeStyle(selectorUnselected);
        if (!ignoreData) setCurrentData(<TopTable items={topTracks} type={TableType.TrackCount}/>);
    }
    const setToArtist = () => {
        setTrackStyle(selectorUnselected);
        setArtistStyle(selectorSelected);
        setAlbumStyle(selectorUnselected);
        setChartStyle(selectorUnselected);
        setShowCountOrTimeSelector(true);
        setCountStyle(selectorSelected);
        setTimeStyle(selectorUnselected);
        if (!ignoreData) setCurrentData(<TopTable items={topArtists} type={TableType.ArtistCount}/>);
    }
    const setToAlbum = () => {
        setTrackStyle(selectorUnselected);
        setArtistStyle(selectorUnselected);
        setAlbumStyle(selectorSelected);
        setChartStyle(selectorUnselected);
        setShowCountOrTimeSelector(true);
        setCountStyle(selectorSelected);
        setTimeStyle(selectorUnselected);
        if (!ignoreData) setCurrentData(<TopTable items={topAlbums} type={TableType.AlbumCount}/>);
    }
    const setToChart = () => {
        setTrackStyle(selectorUnselected);
        setArtistStyle(selectorUnselected);
        setAlbumStyle(selectorUnselected);
        setChartStyle(selectorSelected);
        setShowCountOrTimeSelector(false);
        if (!ignoreData) setCurrentData(<AllCharts
            averageLength={averageLength} averagePopularity={averagePopularity} averageYear={averageYear}
            decadeBreakdown={decadeBreakdown} hourBreakdown={hourBreakdown} medianYear={medianYear}
            modeYear={modeYear} percentExplicit={percentExplicit} totalMinutes={totalMinutes}
            totalTracks={totalTracks} uniqueAlbums={uniqueAlbums} uniqueArtists={uniqueArtists}
            uniqueTracks={uniqueTracks} weekDayBreakdown={weekDayBreakdown}
        />);
    }
    const setToCount = () => {
        setCountStyle(selectorSelected);
        setTimeStyle(selectorUnselected);
        if (!ignoreData) {
            if (trackStyle === selectorSelected) setCurrentData(<TopTable items={topTracks} type={TableType.TrackCount}/>);
            else if (artistStyle === selectorSelected) setCurrentData(<TopTable items={topArtists} type={TableType.ArtistCount}/>);
            else if (albumStyle === selectorSelected) setCurrentData(<TopTable items={topAlbums} type={TableType.AlbumCount}/>);
        }
    }
    const setToTime = () => {
        setCountStyle(selectorUnselected);
        setTimeStyle(selectorSelected);
        if (!ignoreData) {
            if (trackStyle === selectorSelected) setCurrentData(<TopTable items={topTracksTime} type={TableType.TrackTime}/>);
            else if (artistStyle === selectorSelected) setCurrentData(<TopTable items={topArtistsTime} type={TableType.ArtistTime}/>);
            else if (albumStyle === selectorSelected) setCurrentData(<TopTable items={topAlbumsTime} type={TableType.AlbumTime}/>);
        }
    }
    const setToUserSpecific = () => {
        setUserSpecificStyle(selectorSelected);
        setUserAgnosticStyle(selectorUnselected);
        if (useUserAgnosticStats) toggleUserAgnosticStats();
    }
    const setToUserAgnostic = () => {
        setUserSpecificStyle(selectorUnselected);
        setUserAgnosticStyle(selectorSelected);
        if (!useUserAgnosticStats) toggleUserAgnosticStats();
    }

    const toggleUserAgnosticStats = () => {
        setUseUserAgnosticStats(!useUserAgnosticStats);
        setShowControlPanel(false);
        if (timeRange === TimeRange.Custom && (!startTime || !endTime)) setTimeRange(TimeRange.AllTime);
        if (!ignoreData) setCurrentData(<StatsInfo text={'Loading...'}/>);
    }

    const submitTimes = (potStartTime: number, potEndTime: number) => {
        if (validateTimes(potStartTime, potEndTime)) {
            if (potStartTime === startTime && potEndTime === endTime) return;
            setStartTime(potStartTime);
            setEndTime(potEndTime);
            setShowControlPanel(false);
            setCurrentData(<StatsInfo text={'Loading...'}/>);
        } else {
            setShowControlPanel(true);
            setCurrentData(<StatsInfo text={'Invalid time range, try again.'}/>);
        }
    }

    const Dropdown = () => {
        const [isOpen, setIsOpen] = useState(false);

        useEffect(() => {
            document.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                if (!target.classList.toString().includes('dropdown-time')) setIsOpen(false);
            });
        }, []);

        const toggle = () => {
            setIsOpen(!isOpen);
        }

        const itemClicked = (newTimeRange: TimeRange) => {
            setTimeRange(newTimeRange);
            toggle();
        }

        return (
            <div className={'w-full text-center'}>
                <div className='dropdown-time relative inline-block mx-auto'>
                    {isOpen && (
                        <div className='dropdown-time absolute top-full right-0 p-2 bg-mm-text shadow-md rounded z-[1]'>
                            <ul className={'list-none p-0 m-0'}>
                                {DEFAULT_TIME_RANGES.map(range => (
                                    <li className={'dropdown-time p-2 cursor-pointer rounded-[0.2rem] hover:bg-mm-text-hover'} key={range} onClick={() => {
                                        itemClicked(range);
                                    }}>{range}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className={'dropdown-time flex items-center justify-center p-[0.55rem] bg-mm-text rounded-[0.35rem] cursor-pointer w-48'} onClick={toggle}>
                        {timeRange.toString()}
                    </div>
                </div>
            </div>
        );
    }

    const SearchBar = () => {
        const [searchTerm, setSearchTerm] = useState<string>('');
        const debounceValue = useDebounce(searchTerm, 1000);
        const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
        const [resultsVisible, setResultsVisible] = useState<boolean>(false);

        useEffect(() => {
            document.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                if (!isDescendantOfElementWithClass(target, 'search-bar-and-results')) setResultsVisible(false);
            });
        }, []);

        useEffect(() => {
            if (debounceValue === '') {
                setSearchResults([]);
                return;
            }
            doSearch(debounceValue).then(data => {
                if (data === undefined) {
                    setSearchResults([]);
                    return;
                }
                const results: SearchResult[] = data.results as SearchResult[];
                setSearchResults(results);
            });
        }, [debounceValue]);

        useEffect(() => {
            if (searchResults.length > 0) setResultsVisible(true);
            else setResultsVisible(searchTerm.length !== 0);
        }, [searchResults, searchTerm.length]);

        const isDescendantOfElementWithClass = (target: HTMLElement | null, className: string): boolean => {
            while (true) {
                if (!target) return false;
                if (target.classList.toString().includes(className)) return true;
                target = target.parentElement;
            }
        }

        const getEntityTypeFromSearchResult = (item: SearchResult): EntityType => {
            if (!item.image) return EntityType.Artist;
            return item.isTrack ? EntityType.Track : EntityType.Album;
        }

        return (
            <div className={'search-bar-and-results relative'}>
                {resultsVisible && (
                    <div className={'custom-scrollbar absolute top-[120%] right-0 p-[0.3rem] flex flex-col gap-[0.3rem] rounded w-80 max-h-[40rem] overflow-y-scroll bg-mm-dark-2 text-mm-text'}>
                        {searchResults.map((item: SearchResult) => (
                            <div
                                key={item.id}
                                className={'flex flex-row items-center gap-2 p-2 rounded cursor-pointer hover:bg-mm-dark-3'}
                                onClick={() => setEntityModal(
                                    <EntityModal entityType={getEntityTypeFromSearchResult(item)} id={item.id} name={item.name} image={item.image} closeCallback={() => setEntityModal(undefined)} />
                                )}
                            >
                                {item.image ? (
                                    <img src={item.image} style={{width: '2rem'}} alt={'An album cover'}/>
                                ) : (
                                    <FontAwesomeIcon width={'2rem'} size={'xl'} icon={faUser} />
                                )}
                                <div className={'flex flex-col'}>
                                    <div>{item.name}</div>
                                    <div style={{fontSize: 'x-small'}}>
                                        {item.artists ? ((item.isTrack ? '' : 'Album - ') + item.artists.join(", ")) : ('Artist')}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {searchResults.length === 0 && (
                            <div className={'h-40 text-mm-gray m-auto flex items-center'}>No results found...</div>
                        )}
                    </div>
                )}
                <input
                    className={'bg-transparent text-mm-text border border-mm-gray rounded-[0.35rem] p-[0.55rem] portrait:w-[95%]'}
                    placeholder={'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {if (searchResults.length > 0) setResultsVisible(true)}}
                />
            </div>
        );
    }

    const getDataSelector = () => {
        return (
            <div className={'flex cursor-pointer mx-auto justify-center portrait:w-[85%]'}>
                <div className={`${trackStyle} ${selectorOption} ${cornerLeft}`} onClick={setToTrack}>Tracks</div>
                <div className={`${artistStyle} ${selectorOption}`} onClick={setToArtist}>Artists</div>
                <div className={`${albumStyle} ${selectorOption}`} onClick={setToAlbum}>Albums</div>
                <div className={`${chartStyle} ${selectorOption} ${cornerRight}`} onClick={setToChart}>Other</div>
            </div>
        );
    }

    const getCountOrTimeSelector = () => {
        return (
            <div className={`flex cursor-pointer mx-auto justify-center portrait:w-[85%] ${showCountOrTimeSelector ? '' : 'invisible'}`}>
                <div className={`${countStyle} ${selectorOption} ${cornerLeft}`} onClick={setToCount}>
                    <FontAwesomeIcon icon={faHashtag}/>
                </div>
                <div className={`${timeStyle} ${selectorOption} ${cornerRight}`} onClick={setToTime}>
                    <FontAwesomeIcon icon={faClock}/>
                </div>
            </div>
        );
    }

    const getUserAgnosticSelector = () => {
        return (
            <div className={'flex cursor-pointer mx-auto justify-center portrait:w-[85%]'}>
                <div className={`${userSpecificStyle} ${selectorOption} ${cornerLeft}`} onClick={setToUserSpecific}>
                    <FontAwesomeIcon icon={faUser}/>
                </div>
                <div className={`${userAgnosticStyle} ${selectorOption} ${cornerRight}`} onClick={setToUserAgnostic}>
                    <FontAwesomeIcon icon={faGlobe}/>
                </div>
            </div>
        );
    }

    const getCustomTimeRangeSelectors = () => {
        return (
            <div className={'flex items-center text-mm-text'}>
                <div className={'w-28 flex [&>div>div.react-datepicker__input-container]:flex [&>div>div.react-datepicker__input-container]:w-full [&>div>div.react-datepicker__input-container_input]:flex [&>div>div.react-datepicker__input-container_input]:w-full'}>
                    <DatePicker className={'w-full text-center border border-mm-text rounded bg-mm-dark text-mm-text text-base'} selected={selectedStartDate} onChange={(date: Date) => setSelectedStartDate(date)}/>
                </div>
                <div style={{margin: '0 .3rem'}}>to</div>
                <div className={'w-28 flex [&>div>div.react-datepicker__input-container]:flex [&>div>div.react-datepicker__input-container]:w-full [&>div>div.react-datepicker__input-container_input]:flex [&>div>div.react-datepicker__input-container_input]:w-full'}>
                    <DatePicker className={'w-full text-center border border-mm-text rounded bg-mm-dark text-mm-text text-base'} selected={selectedEndDate} onChange={(date: Date) => setSelectedEndDate(date)}/>
                </div>
                <div className={'text-mm-dark bg-mm-green cursor-pointer py-[0.1rem] px-2 rounded-[0.3rem] text-center ml-2 hover:bg-mm-text'} onClick={() => submitTimes(dateToMillis(selectedStartDate), dateToMillis(selectedEndDate) + 86399999)}>
                    <b>GO</b>
                </div>
            </div>
        );
    }

    const getControlPanelLandscape = () => {
        return (
            <>
                <div className={'flex flex-row w-[90%] items-start mx-auto mb-[0.6rem] justify-between'}>
                    <div style={{display: 'flex', flexDirection: 'row', gap: '1.5rem'}}>
                        <Dropdown/>
                        {getDataSelector()}
                        {getCountOrTimeSelector()}
                        {getUserAgnosticSelector()}
                    </div>
                    <SearchBar/>
                </div>
                <div className={'flex flex-row w-[90%] items-start mx-auto pb-[0.6rem] border-b border-mm-gray'}>
                    {timeRange === TimeRange.Custom && getCustomTimeRangeSelectors()}
                </div>
            </>
        );
    }

    const getControlPanelPortrait = () => {
        return (
            <div className={'flex flex-col items-center gap-4 mx-auto'}>
                <Dropdown/>
                {timeRange === TimeRange.Custom && getCustomTimeRangeSelectors()}
                {getDataSelector()}
                <div style={{display: 'flex', flexDirection: 'row', gap: '1rem'}}>
                    {getCountOrTimeSelector()}
                    {getUserAgnosticSelector()}
                </div>
                <SearchBar/>
            </div>
        );
    }

    return (
        <div>
            {showControlPanel && (isLandscape ? getControlPanelLandscape() : getControlPanelPortrait())}
            {currentData}
            {popupVisible && <ExtendedStreamingInfo callback={() => setPopupVisible(false)}/>}
            {entityModal && entityModal}
        </div>
    );
}

const StatsInfo = (props: InfoProps) => {
    return (
        <div className={'mt-8 text-mm-cyan flex flex-col items-center justify-center text-[2rem] w-full'}>{props.text}</div>
    );
}

// HELPER FUNCTIONS
const addRankColumn = (items: TopItem[]) => {
    let rank = 1;
    items.forEach(item => {
        item.rank = rank;
        rank++;
    });
}

const addEntityModalSetterToArray = (setter: (data: React.ReactElement) => void, items: any[]) => {
    items.forEach(item => {
        item.setEntityModal = setter;
    });
}

const convertDecadesToPieChartData = (data: DecadeBreakdown[]) => {
    let result = [];
    result.push(['Decade', 'Count']);
    data.forEach(item => {
        result.push([item.decade, item.count]);
    });
    return result;
}

const makeIntDoubleDigit = (num: number) => {
    if (num < 10) return '0' + num;
    return num;
}

const validateTimes = (startTime: number, endTime: number) => {
    if (isNaN(startTime) || isNaN(endTime)) return false;
    if (startTime < 0) return false;
    if (endTime < startTime) return false;
    if (endTime < 1145746800000) return false;
    return true;
}

const dateToMillis = (date: Date) => {
    const beginningOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return beginningOfDay.getTime();
}

const addWeekDayToData = (data: number[]): any[] => {
    let result = [];
    result.push(['Day', 'Count']);
    result.push(['Sunday', data[0]]);
    result.push(['Monday', data[1]]);
    result.push(['Tuesday', data[2]]);
    result.push(['Wednesday', data[3]]);
    result.push(['Thursday', data[4]]);
    result.push(['Friday', data[5]]);
    result.push(['Saturday', data[6]]);
    return result;
}
